import React, { useContext, useEffect, useState } from 'react';
import { AuthenticationContext } from './AuthenticationProvider';
// @ts-ignore
import { createConsumer } from '@rails/actioncable';
import { copyObject } from '../utility';
import moment from 'moment/moment';

export interface IActionCableContext {
  onSubscribe(_projectId: number): void;
  onClearMessages(_projectId: number): void;

  available: boolean;
  messages: Messages;
}

export interface IActionCableProviderProps {
  children: any;
}

const ActionCableContext = React.createContext<IActionCableContext>({
  onSubscribe(_projectId: number) {},
  onClearMessages(_projectId: number) {},

  available: false,
  messages: [],
});

interface Subscription {
  channelName: string;
  projectId: number;
  isConnected: boolean;
}

interface Subscriptions {
  [channel: string]: Subscription;
}

interface Message {
  id: string;
  channel: string;
  projectId: number;
  message: string;
}

interface Messages {
  [projectId: number]: Message[];
}

const ActionCableProvider = (props: IActionCableProviderProps) => {
  const { children } = props;

  const { token } = useContext(AuthenticationContext);

  const [actionCable, setActionCable] = useState<any | undefined>();
  const [subscriptions, setSubscriptions] = useState<Subscriptions>({});
  const [messages, setMessages] = useState<Messages>({});
  const [available, setAvailable] = useState<boolean>(false);

  useEffect(() => {
    if (token && token.length > 0) {
      const cable: any = createConsumer(
        `${process.env.REACT_APP_CABLE_URL}?token=${token}`
      );

      if (cable) {
        setActionCable(cable);
        setAvailable(true);
      }
    }
  }, [token]);

  const onConnectedSubscription = (projectId: number) => {
    if(subscriptions[projectId]) {
      let copy = copyObject(subscriptions);
      copy[projectId].connected = true;
      setSubscriptions(copy);
    }
  };

  const onDisconnectedSubscription = (projectId: number) => {
    if(subscriptions[projectId]) {
      let copy = copyObject(subscriptions);
      copy[projectId].connected = false;
      setSubscriptions(copy);
    }
  };

  const onReceivedMessage = (projectId: number, data: any) => {
    let copy = copyObject(messages);

    if(!copy[projectId]) {
      copy[projectId] = [];
    }

    copy[projectId].push({
      id: `${projectId}-${moment().format()}`,
      channel: 'NotificationChannel',
      message: data as string,
    });

    setMessages(copy);
  };

  const onSubscribe = (projectId: number) => {
    if (actionCable) {
      if (!subscriptions[projectId]) {
        actionCable.subscriptions.create(
          {
            channel: 'NotificationChannel',
            project_id: projectId,
          },
          {
            received(data: any) {
              onReceivedMessage(projectId, data);
            },
            connected() {
              onConnectedSubscription(projectId);
            },
            disconnected() {
              onDisconnectedSubscription(projectId);
            },
          }
        );

        const obj: Subscription = {
          channelName: 'NotificationChannel',
          projectId: projectId,
          isConnected: false,
        };

        let copy = copyObject(subscriptions);
        copy[projectId] = obj;
        setSubscriptions(copy);

        let messagesCopy = copyObject(messages);
        messagesCopy[projectId] = [];
        setMessages(messagesCopy);
      }
    }
  };

  const onClearMessages = (projectId: number) => {
    let copy = copyObject(messages);

    copy[projectId].length = 0;
    setMessages(copy);
  };

  return (
    <ActionCableContext.Provider
      value={{
        onSubscribe,
        onClearMessages,

        available,
        messages
      }}
    >
      {children}
    </ActionCableContext.Provider>
  );
};

export { ActionCableContext, ActionCableProvider };
